import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import jwt_decode from "jwt-decode";
import { useLocation } from 'react-router-dom'

import ModalMessage from '../components/ModalMessage';

import useQuery from './useQuery';
import { Brands } from '../utils/Brands'
import { userToken } from '../utils/localStorage'

import MaisConteudoApi from '../services/MaisConteudoAPi'

interface IUserInfoState {
    exp: number;
    iat: number;
    name: string;
    email: string
    code: string;
    brands: { id_marca: number, marca: string, id_marca_linx: number, id_marca_grupo: number }[]
}

interface IUserContext {
    isLogged: boolean;
    onLoginHandler: (sellerCode: string, cpf: string, password: string) => void;
    onLogoutHandler: () => void;
    userInfo: IUserInfoState;
    userBrandsMemo: {
        filterItemName: string;
        filterItemValue: string;
    }[],
    error: IErrorState;
    onGoToMaisVendasLogin: () => void;
}
interface IErrorState {
    error: string;
    isToShowInput: boolean;
}

export const UserContext = createContext({} as IUserContext);

const UserContextProvider: React.FC = ({ children }) => {

    const expTimeout = useRef<NodeJS.Timeout>()

    const [isLogged, setIsLogged] = useState<boolean>(!!userToken)
    const [userInfo, setUserInfo] = useState<IUserInfoState>({ exp: 0, iat: 0, code: '', name: '', email: '', brands: [] })
    const [error, setError] = useState<IErrorState>({ error: '', isToShowInput: false })

    const location = useLocation()
    const jwtToken = useQuery().get('token')

    const userBrandsMemo = useMemo(() => (
        userInfo.brands.map((userBrands) => Brands.filter((brand) => userBrands.marca === brand.backendName))
            .flatMap(item => item)
            .map(userBrand => ({ filterItemName: userBrand.name, filterItemValue: userBrand.vtexName, brands: userBrand.brands }))
    ), [userInfo.brands])

    const shouldAuth = useMemo(() => {
        const pagesShouldBeAuth = ['/my-wishlists', '/products', '/create-wishlist', '/edit-wishlist']
        return !pagesShouldBeAuth.find(link => location.pathname.includes(link))
    }, [location.pathname])

    const onGoToMaisVendasLogin = useCallback(() => {
        return window.location.replace('https://maisvendas.somalabs.com.br/login')
    }, [])

    const onLoginHandler = useCallback(async (sellerCode, cpf, password) => {
        try {
            setError({ error: '', isToShowInput: false })
            const res = await MaisConteudoApi.post("v1/users", { sellerCode, cpf, password })
            const decode: IUserInfoState = jwt_decode(res.data.userToken);
            setUserInfo(decode)
            localStorage.setItem('userToken', res.data.userToken)
            setIsLogged(true)
        } catch (error: any) {
            setError({
                error: error.response?.data.message || "Algo de errado aconteceu, por favor tente novamente!",
                isToShowInput: true
            })
        }
    }, [])

    const onSetLoginByTokenHandler = useCallback((token: string) => {
        const decode: IUserInfoState = jwt_decode(token);
        setUserInfo(decode)
        localStorage.setItem('userToken', token)
        setIsLogged(true)
    }, [])

    const onLogoutHandler = useCallback(() => {
        localStorage.removeItem('userToken')
        localStorage.removeItem('env')
        setIsLogged(false)
        setUserInfo({ exp: 0, iat: 0, code: '', name: '', email: '', brands: [] })
        setError({ error: 'Sua sessão expirou, Por favor se autentique novamente!', isToShowInput: true })
    }, [])

    const onValidateTokenHandler = useCallback(() => {
        let informationsToken = {
            isValid: false,
            expTime: 0
        }

        const millisecondsExpTime = userInfo.exp * 1000
        const currentMillisecondsTime = new Date().getTime()

        informationsToken = {
            isValid: currentMillisecondsTime < millisecondsExpTime,
            expTime: millisecondsExpTime - currentMillisecondsTime
        }

        return informationsToken
    }, [userInfo.exp])

    const onAutoLoginWithParam = useCallback(async () => {
        if (!jwtToken || shouldAuth) return

        try {
            const res = await MaisConteudoApi.post('v1/users/token-mais-vendas', { token: jwtToken })
            onSetLoginByTokenHandler(res.data.token)
        } catch (error: any) {
            setError({
                error: error.response?.data.message || "Algo de errado aconteceu, por favor tente novamente!",
                isToShowInput: true
            })
        }
    }, [jwtToken, onSetLoginByTokenHandler, shouldAuth])

    const onTryToLogin = useCallback(() => {
        if (userToken) {
            onSetLoginByTokenHandler(userToken)

        } else if (jwtToken) {
            onAutoLoginWithParam()

        } else if (!shouldAuth) {
            onGoToMaisVendasLogin()
        }
    }, [jwtToken, onAutoLoginWithParam, onGoToMaisVendasLogin, onSetLoginByTokenHandler, shouldAuth])

    const onCheckExpTime = useCallback(() => {
        if (!isLogged || shouldAuth || !userInfo.exp) return
        if (expTimeout.current) clearTimeout(expTimeout.current)

        const { isValid, expTime } = onValidateTokenHandler()

        if (isValid) {
            return expTimeout.current = setTimeout(onLogoutHandler, expTime)
        } else if (jwtToken) {
            onAutoLoginWithParam()
        } else {
            localStorage.removeItem('userToken')
            localStorage.removeItem('env')
            onGoToMaisVendasLogin()
        }
    }, [isLogged, shouldAuth, userInfo.exp, onValidateTokenHandler, jwtToken, onLogoutHandler, onAutoLoginWithParam, onGoToMaisVendasLogin])

    useEffect(() => {
        onTryToLogin()
    }, [onTryToLogin])

    useEffect(() => {
        onCheckExpTime()
    }, [onCheckExpTime])

    return (
        <UserContext.Provider value={{
            isLogged,
            onLoginHandler,
            onLogoutHandler,
            userInfo,
            userBrandsMemo,
            error,
            onGoToMaisVendasLogin
        }}>
            {
                shouldAuth &&
                <ModalMessage
                    optionSVG='errorImage'
                    isOpen={error.isToShowInput}
                    tittle="Oops..."
                    textWishList={error.error}
                    onClick={onGoToMaisVendasLogin}
                    buttonLabel="Fazer login"
                    classNameModal='priority_modal'
                />
            }
            {children}
        </UserContext.Provider>
    )
}

const useUser = () => {
    const userContext = useContext(UserContext)
    return { ...userContext }
}

export default useUser

export { UserContextProvider }
